// frontend/src/pages/MatchScoring/useMatchState.js
//
// Polls GET /scorer/matches/:id/state every ~2.5s and exposes:
//   { state, error, refresh, derived }
//
// `derived` builds the things the UI needs that the raw state doesn't
// give us directly:
//   - currentInnings (the live one, or last one if all completed)
//   - liveStateForCurrent
//   - currentDrsChances (map by member_id+role)
//
// Polling pauses while the tab is hidden (visibility API), resumes on focus.
// On a successful POST elsewhere, the caller can call refresh() to skip the
// next interval.

import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../../utils/api';

const POLL_MS = 2500;

export default function useMatchState(matchId) {
  const [state, setState]   = useState(null);
  const [error, setError]   = useState(null);
  const [loaded, setLoaded] = useState(false);
  const inflightRef = useRef(false);
  const timerRef    = useRef(null);
  const mountedRef  = useRef(true);

  const fetchOnce = useCallback(async () => {
    if (!matchId)            return;
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const res = await api.get(`/scorer/matches/${matchId}/state`);
      if (!mountedRef.current) return;
      setState(res.data);
      setError(null);
      setLoaded(true);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e?.response?.data?.error || e.message || 'state load failed');
    } finally {
      inflightRef.current = false;
    }
  }, [matchId]);

  // Setup polling
  useEffect(() => {
    mountedRef.current = true;
    fetchOnce();
    function tick() {
      if (document.visibilityState === 'visible') fetchOnce();
      timerRef.current = setTimeout(tick, POLL_MS);
    }
    timerRef.current = setTimeout(tick, POLL_MS);

    function onVis() { if (document.visibilityState === 'visible') fetchOnce(); }
    document.addEventListener('visibilitychange', onVis);

    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchOnce]);

  // Derived view
  const derived = (() => {
    if (!state) return null;
    const innings = state.innings || [];
    const live = innings.find(i => i.status === 'live');
    const last = innings.length > 0 ? innings[innings.length - 1] : null;
    const currentInnings = live || last;
    const liveStateForCurrent = currentInnings
      ? (state.live_state || []).find(ls => ls.innings_id === currentInnings.id) || null
      : null;

    // DRS chances by "memberId|role" composite key
    const drsByKey = new Map();
    (state.drs_chances || []).forEach(c => {
      drsByKey.set(`${c.member_id}|${c.role}`, c.chances_remaining);
    });

    // Push 2 (Session 21 — 26 May 2026): time at crease per active batter.
    // Map: member_id → first_seen_at (ISO string from API, may be null if
    // batter has just walked in and not seen a ball yet).
    const batterFirstSeenById = new Map();
    (state.batter_minutes || []).forEach(r => {
      // Only include current innings (the polled state may contain rows
      // for both innings if both have live_match_state seeded; the strip
      // only renders the current innings's batters anyway).
      if (currentInnings && r.innings_id !== currentInnings.id) return;
      batterFirstSeenById.set(r.member_id, r.first_seen_at);
    });

    return { currentInnings, liveStateForCurrent, drsByKey, batterFirstSeenById };
  })();

  return { state, error, loaded, refresh: fetchOnce, derived };
}

// ── Push 2 (Session 21 — 26 May 2026): a 1-Hz tick for live time displays.
// Returns a Date that updates every second. Callers compose elapsed strings
// like (now - innings.started_at). Pauses while tab is hidden so we don't
// burn React renders in the background.
export function useNowTick() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let id = null;
    function start() {
      if (id != null) return;
      id = setInterval(() => setNow(new Date()), 1000);
    }
    function stop() {
      if (id == null) return;
      clearInterval(id);
      id = null;
    }
    function onVis() {
      if (document.visibilityState === 'visible') {
        setNow(new Date());  // immediate refresh on focus
        start();
      } else {
        stop();
      }
    }
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
  return now;
}

// Format helpers (exported so other components can use the same format)
// Push 2: "Started h:mm AM/PM IST" from a TIMESTAMPTZ.
export function fmtStartedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  // Render in Asia/Kolkata regardless of viewer's TZ, with "IST" suffix.
  const t = d.toLocaleTimeString('en-US', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `Started ${t} IST`;
}

// Push 2: elapsed time as H:MM:SS or MM:SS depending on whether hours > 0.
export function fmtElapsed(fromIso, now) {
  if (!fromIso) return '0:00';
  const startMs = new Date(fromIso).getTime();
  const nowMs   = (now instanceof Date ? now.getTime() : Date.now());
  let sec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
  const h = Math.floor(sec / 3600); sec -= h * 3600;
  const m = Math.floor(sec / 60);   sec -= m * 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}
